import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Send, Filter, Loader2, CheckCircle, Info, Play, ExternalLink,
  Users, Mic, Sparkles, Disc3, PenTool, Music, ChevronDown, X,
  AlertCircle, Eye, ThumbsUp
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { usePageVisibility } from '../hooks/usePageVisibility';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  groupe: Users,
  soliste: Mic,
  nouveau: Sparkles,
  album: Disc3,
  compositeur: PenTool,
  titre: Music,
};

const Proposer = () => {
  const [searchParams] = useSearchParams();
  usePageVisibility('page_proposer_enabled');
  const [categories, setCategories] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [phase, setPhase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('proposal_count');
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showRules, setShowRules] = useState(false);
  
  const [formData, setFormData] = useState({
    category_id: searchParams.get('category') || '',
    nominee_name: '',
    nominee_type: '',
    description: '',
    justification: '',
    video_url: '',
    spotify_url: '',
    website_url: '',
    image_url: '',
    proposer_name: '',
    proposer_email: '',
    accepted_rules: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (searchParams.get('category')) {
      setFormData(prev => ({ ...prev, category_id: searchParams.get('category') }));
      setSelectedCategory(searchParams.get('category'));
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [catRes, propRes, phaseRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/proposals?status=approved`),
        axios.get(`${API}/phase`)
      ]);
      setCategories(catRes.data);
      setProposals(propRes.data);
      setPhase(phaseRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.accepted_rules) {
      toast.error('Veuillez accepter le règlement');
      return;
    }
    if (!formData.nominee_name.trim()) {
      toast.error('Veuillez entrer un nom');
      return;
    }
    if (!formData.video_url.trim()) {
      toast.error('Une vidéo est fortement recommandée');
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/proposals`, formData);
      
      if (response.data.is_duplicate) {
        toast.success('Cette proposition existe déjà ! Votre soutien a été ajouté.');
      } else {
        toast.success('Proposition soumise avec succès ! Elle sera modérée avant publication.');
      }
      
      setFormData({
        category_id: '',
        nominee_name: '',
        nominee_type: '',
        description: '',
        justification: '',
        video_url: '',
        spotify_url: '',
        website_url: '',
        image_url: '',
        proposer_name: '',
        proposer_email: '',
        accepted_rules: false
      });
      setSelectedCategory('');
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de la soumission';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setFormData(prev => ({ ...prev, category_id: value }));
  };

  const getCategoryData = (catId) => {
    return categories.find(c => c.id === catId);
  };

  const filteredProposals = proposals
    .filter(p => filterCategory === 'all' || p.category_id === filterCategory)
    .sort((a, b) => {
      if (sortBy === 'proposal_count') return b.proposal_count - a.proposal_count;
      if (sortBy === 'nominee_name') return a.nominee_name.localeCompare(b.nominee_name);
      if (sortBy === 'created_at') return new Date(b.created_at) - new Date(a.created_at);
      return 0;
    });

  const isPhase1Open = phase?.phase1_open !== false;
  const currentCategoryData = getCategoryData(selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian pt-32 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-obsidian pt-32 pb-20" data-testid="proposer-page">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mb-12">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 border border-gold/30 bg-gold/5">
            <span className="text-xs uppercase tracking-widest text-gold">Phase 1 — Propositions</span>
          </div>
          <h1 className="font-syne text-4xl md:text-5xl font-extrabold tracking-tighter uppercase text-white mb-4">
            Proposer un<br />
            <span className="text-gold-gradient">Candidat</span>
          </h1>
          <p className="text-lg text-white/60">
            La phase 1 permet au public de proposer des artistes, groupes ou œuvres dans les 6 catégories officielles. 
            Vous êtes au cœur du processus ! Chaque proposition validée sera ensuite soumise au vote final.
          </p>
        </div>
        
        {!isPhase1Open && (
          <div className="mt-6 p-4 border border-orange-500/30 bg-orange-500/10 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0" />
            <p className="text-orange-300">
              {phase?.phase2_open 
                ? "La phase de propositions est terminée. Le vote final est en cours !" 
                : phase?.closed_message || "La phase de propositions est fermée."}
            </p>
          </div>
        )}
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <div className={`sticky top-28 ${!isPhase1Open ? 'opacity-50 pointer-events-none' : ''}`}>
              <div className="p-6 md:p-8 border border-white/10 bg-obsidian-light">
                <h2 className="font-syne text-xl font-bold text-white mb-6">Nouvelle Proposition</h2>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Category Selector */}
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Catégorie *</label>
                    <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                      <SelectTrigger className="w-full bg-obsidian border-white/10 text-white" data-testid="category-select">
                        <SelectValue placeholder="Choisir une catégorie" />
                      </SelectTrigger>
                      <SelectContent className="bg-obsidian-light border-white/10 z-50">
                        {categories.map(cat => {
                          const Icon = categoryIcons[cat.id] || Music;
                          return (
                            <SelectItem key={cat.id} value={cat.id} className="text-white hover:bg-white/5">
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4 text-gold" />
                                {cat.name}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Dynamic Rules Display */}
                  {currentCategoryData && (
                    <div className="p-4 border border-gold/20 bg-gold/5 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-syne font-bold text-gold text-sm">{currentCategoryData.name}</h4>
                        <button 
                          type="button"
                          onClick={() => setShowRules(!showRules)}
                          className="text-xs text-gold/70 hover:text-gold flex items-center gap-1"
                        >
                          {showRules ? 'Masquer' : 'Voir le règlement'} 
                          <ChevronDown className={`w-3 h-3 transition-transform ${showRules ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                      <p className="text-sm text-white/60">{currentCategoryData.description}</p>
                      
                      {showRules && (
                        <div className="pt-3 border-t border-gold/20 space-y-3 text-xs">
                          <div>
                            <p className="text-gold/80 font-medium mb-1">Critères d'éligibilité :</p>
                            <ul className="space-y-1 text-white/50">
                              {currentCategoryData.eligibility?.map((e, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                                  {e}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <p className="text-gold/80 font-medium mb-1">Accepté :</p>
                            <ul className="space-y-1 text-white/50">
                              {currentCategoryData.accepted?.map((a, i) => (
                                <li key={i}>• {a}</li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <p className="text-red-400/80 font-medium mb-1">Non accepté :</p>
                            <ul className="space-y-1 text-white/50">
                              {currentCategoryData.excluded?.map((e, i) => (
                                <li key={i}>• {e}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Nominee Name */}
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Nom du candidat *</label>
                    <input
                      type="text"
                      value={formData.nominee_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, nominee_name: e.target.value }))}
                      placeholder="Ex: Denez Prigent, Bagad Kemper..."
                      className="w-full px-4 py-3 bg-obsidian border border-white/10 text-white placeholder:text-white/30 focus:border-gold/50 transition-colors"
                      data-testid="nominee-name-input"
                      required
                    />
                  </div>

                  {/* Nominee Type */}
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Type de candidature</label>
                    <Select value={formData.nominee_type} onValueChange={(v) => setFormData(prev => ({ ...prev, nominee_type: v }))}>
                      <SelectTrigger className="w-full bg-obsidian border-white/10 text-white">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent className="bg-obsidian-light border-white/10 z-50">
                        <SelectItem value="groupe" className="text-white">Groupe / Ensemble</SelectItem>
                        <SelectItem value="artiste" className="text-white">Artiste solo</SelectItem>
                        <SelectItem value="album" className="text-white">Album</SelectItem>
                        <SelectItem value="titre" className="text-white">Titre / Morceau</SelectItem>
                        <SelectItem value="autre" className="text-white">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Description courte *</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Décrivez brièvement l'artiste ou l'œuvre..."
                      rows={3}
                      className="w-full px-4 py-3 bg-obsidian border border-white/10 text-white placeholder:text-white/30 focus:border-gold/50 transition-colors resize-none"
                      required
                    />
                  </div>

                  {/* Justification */}
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Pourquoi cette proposition ?</label>
                    <textarea
                      value={formData.justification}
                      onChange={(e) => setFormData(prev => ({ ...prev, justification: e.target.value }))}
                      placeholder="Expliquez pourquoi ce candidat mérite cette distinction..."
                      rows={2}
                      className="w-full px-4 py-3 bg-obsidian border border-white/10 text-white placeholder:text-white/30 focus:border-gold/50 transition-colors resize-none"
                    />
                  </div>

                  {/* Video URL - Important */}
                  <div className="p-4 border border-electric-blue/30 bg-electric-blue/5">
                    <label className="flex items-center gap-2 text-sm text-electric-cyan mb-2">
                      <Play className="w-4 h-4" />
                      Lien vidéo (YouTube / Vimeo) *
                    </label>
                    <input
                      type="url"
                      value={formData.video_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                      placeholder="https://youtube.com/watch?v=..."
                      className="w-full px-4 py-3 bg-obsidian border border-white/10 text-white placeholder:text-white/30 focus:border-electric-blue/50 transition-colors"
                      data-testid="video-url-input"
                    />
                    <p className="text-xs text-white/40 mt-2">
                      La vidéo permet au public de découvrir l'univers artistique. Fortement recommandé !
                    </p>
                  </div>

                  {/* Optional Links */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-white/50 mb-1">Spotify / Deezer</label>
                      <input
                        type="url"
                        value={formData.spotify_url}
                        onChange={(e) => setFormData(prev => ({ ...prev, spotify_url: e.target.value }))}
                        placeholder="https://..."
                        className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm placeholder:text-white/30"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-white/50 mb-1">Site / Réseaux</label>
                      <input
                        type="url"
                        value={formData.website_url}
                        onChange={(e) => setFormData(prev => ({ ...prev, website_url: e.target.value }))}
                        placeholder="https://..."
                        className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm placeholder:text-white/30"
                      />
                    </div>
                  </div>

                  {/* Proposer Info */}
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-xs text-white/40 mb-3">Vos informations (non publiées)</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <input
                          type="text"
                          value={formData.proposer_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, proposer_name: e.target.value }))}
                          placeholder="Votre nom"
                          className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm placeholder:text-white/30"
                          required
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          value={formData.proposer_email}
                          onChange={(e) => setFormData(prev => ({ ...prev, proposer_email: e.target.value }))}
                          placeholder="Votre email"
                          className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm placeholder:text-white/30"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Rules Checkbox */}
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="rules"
                      checked={formData.accepted_rules}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, accepted_rules: checked }))}
                      className="mt-1 border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                    />
                    <label htmlFor="rules" className="text-sm text-white/60 cursor-pointer">
                      J'ai lu et j'accepte le règlement de la catégorie sélectionnée. Je confirme que ma proposition respecte les critères d'éligibilité.
                    </label>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting || !isPhase1Open}
                    className="w-full bg-gold hover:bg-gold-glow text-obsidian font-bold uppercase tracking-widest px-6 py-4 text-sm transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="submit-proposal-btn"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Soumettre ma proposition
                      </>
                    )}
                  </button>
                </form>

                <p className="mt-4 text-xs text-white/40 text-center">
                  Votre proposition sera modérée avant publication.
                </p>
              </div>
            </div>
          </div>

          {/* Proposals Display */}
          <div className="lg:col-span-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <h2 className="font-syne text-2xl font-bold text-white">
                Propositions Validées
                <span className="text-white/40 text-lg ml-2">({filteredProposals.length})</span>
              </h2>
              
              <div className="flex items-center gap-3">
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-40 bg-obsidian border-white/10 text-white text-sm">
                    <Filter className="w-4 h-4 mr-2 text-white/50" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-obsidian-light border-white/10 z-50">
                    <SelectItem value="all" className="text-white">Toutes</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id} className="text-white">{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-32 bg-obsidian border-white/10 text-white text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-obsidian-light border-white/10 z-50">
                    <SelectItem value="proposal_count" className="text-white">Popularité</SelectItem>
                    <SelectItem value="nominee_name" className="text-white">A → Z</SelectItem>
                    <SelectItem value="created_at" className="text-white">Récent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Proposals Grid */}
            {filteredProposals.length === 0 ? (
              <div className="text-center py-16 border border-white/10 bg-obsidian-light">
                <Music className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">Aucune proposition validée pour le moment</p>
                <p className="text-sm text-white/30 mt-2">Soyez le premier à proposer !</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {filteredProposals.map((proposal) => {
                  const Icon = categoryIcons[proposal.category_id] || Music;
                  const catData = getCategoryData(proposal.category_id);
                  
                  return (
                    <div
                      key={proposal.id}
                      className="border border-white/10 bg-obsidian-light hover:border-gold/30 transition-all group cursor-pointer"
                      onClick={() => setSelectedProposal(proposal)}
                      data-testid={`proposal-card-${proposal.id}`}
                    >
                      {/* Thumbnail */}
                      <div className="aspect-video relative overflow-hidden bg-obsidian">
                        {proposal.video_thumbnail ? (
                          <img 
                            src={proposal.video_thumbnail} 
                            alt={proposal.nominee_name}
                            className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gold/10 to-electric-blue/10">
                            <Icon className="w-12 h-12 text-white/20" />
                          </div>
                        )}
                        {proposal.video_url && (
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="w-12 h-12 rounded-full bg-gold/90 flex items-center justify-center">
                              <Play className="w-5 h-5 text-obsidian ml-0.5" />
                            </div>
                          </div>
                        )}
                        {proposal.is_featured && (
                          <div className="absolute top-2 left-2 px-2 py-1 bg-gold text-obsidian text-xs font-bold">
                            À la une
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <h3 className="font-syne font-bold text-white group-hover:text-gold transition-colors line-clamp-1">
                              {proposal.nominee_name}
                            </h3>
                            <p className="text-xs text-white/40">{catData?.name}</p>
                          </div>
                          <div className="flex items-center gap-1 px-2 py-1 bg-gold/10 text-gold text-sm flex-shrink-0">
                            <ThumbsUp className="w-3 h-3" />
                            {proposal.proposal_count}
                          </div>
                        </div>
                        <p className="text-sm text-white/50 line-clamp-2">{proposal.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Proposal Detail Modal */}
      {selectedProposal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-obsidian/90 backdrop-blur-sm" onClick={() => setSelectedProposal(null)}>
          <div 
            className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-obsidian-light border border-white/10"
            onClick={e => e.stopPropagation()}
          >
            {/* Video */}
            {selectedProposal.video_embed_url && (
              <div className="aspect-video bg-obsidian">
                <iframe
                  src={selectedProposal.video_embed_url}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={selectedProposal.nominee_name}
                />
              </div>
            )}
            
            {/* Content */}
            <div className="p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <p className="text-sm text-gold mb-1">{getCategoryData(selectedProposal.category_id)?.name}</p>
                  <h2 className="font-syne text-2xl font-bold text-white">{selectedProposal.nominee_name}</h2>
                </div>
                <button onClick={() => setSelectedProposal(null)} className="p-2 text-white/50 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-white/70 mb-4">{selectedProposal.description}</p>
              
              {selectedProposal.justification && (
                <div className="p-4 border border-white/10 bg-obsidian mb-4">
                  <p className="text-sm text-white/50 mb-1">Pourquoi cette proposition :</p>
                  <p className="text-white/70 italic">"{selectedProposal.justification}"</p>
                </div>
              )}
              
              {/* External Links */}
              <div className="flex flex-wrap gap-3">
                {selectedProposal.spotify_url && (
                  <a href={selectedProposal.spotify_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 border border-green-500/30 text-green-400 text-sm hover:bg-green-500/10 transition-colors">
                    <ExternalLink className="w-4 h-4" /> Spotify
                  </a>
                )}
                {selectedProposal.website_url && (
                  <a href={selectedProposal.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white/70 text-sm hover:bg-white/5 transition-colors">
                    <ExternalLink className="w-4 h-4" /> Site web
                  </a>
                )}
              </div>
              
              <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                <p className="text-sm text-white/40">
                  {selectedProposal.proposal_count} proposition{selectedProposal.proposal_count > 1 ? 's' : ''}
                </p>
                {phase?.phase2_open && selectedProposal.is_finalist && (
                  <Link
                    to="/voter"
                    className="bg-gold hover:bg-gold-glow text-obsidian font-bold uppercase tracking-widest px-6 py-2 text-sm transition-all"
                  >
                    Voter pour ce candidat
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Proposer;
